<template>
  <!--
    The concierge's header (prototype D, 09 §Д-5.1; B2 REQ-040): the avatar — the manager's real photo, else the first
    letter of the name in Garamond on --soft — with the green «на связи» dot, then the name in Inter 500 16 and the
    screen's own line under it (slot). The server names the manager (on duty or assigned); nobody named → «Консьерж».
  -->
  <!-- Spans, not divs: this sits inside the screen's h1/h2, which may hold phrasing content only. -->
  <span class="manager-header" data-testid="support-manager-header" :data-mode="manager.mode">
    <span class="manager-header__avatar" aria-hidden="true">
      <ManagerPhoto v-if="named" :token="manager.photoToken" :name="manager.name" :size="44"
                    class="manager-header__photo" />
      <span class="manager-header__letter">{{ letter }}</span>
      <span class="manager-header__dot" />
    </span>
    <span class="manager-header__text">
      <span v-if="named" class="manager-header__name" data-testid="support-manager-name">{{ manager.name }}</span>
      <span v-else class="manager-header__name" data-testid="support-manager-default">{{ t('cabinet.concierge.defaultName') }}</span>
      <slot name="sub" />
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ManagerView } from '@/api/support';
import ManagerPhoto from './ManagerPhoto.vue';

// No unread count: the header sits over the open chat, whose messages the server marks read as it serves them.
const props = defineProps<{ manager: ManagerView }>();
const { t } = useI18n();
const named = computed(() => props.manager.mode !== 'DEFAULT' && !!props.manager.name);
/** The avatar's letter: the manager's initial, else the concierge's. */
const letter = computed(() => ((named.value ? props.manager.name : t('cabinet.concierge.defaultName')) ?? '')
  .trim().charAt(0).toUpperCase());
</script>

<style scoped lang="scss">
/* Prototype D (concierge-messenger.css, computed at 390 / 1440): avatar 44 px --soft, letter Garamond 26 bronze, a 9 px
   green dot with a 2 px cream ring at its lower right; name Inter 500 16 / -0.02em; 16 px between them. */
.manager-header { display: flex; align-items: center; gap: 16px; min-width: 0; }
.manager-header__avatar {
  position: relative; flex: none; width: 44px; height: 44px; border-radius: 50%;
  display: grid; place-items: center; background: var(--soft); color: var(--bronze);
  font-family: var(--font-heading); font-size: 26px; font-weight: 400; line-height: 1;
}
/* A real photo covers the letter; without one (or until it loads) the letter shows. */
.manager-header__photo { position: absolute; inset: 0; width: 44px; height: 44px; border-radius: 50%; object-fit: cover; }
.manager-header__dot {
  position: absolute; right: 1px; bottom: 1px; width: 9px; height: 9px; border-radius: 50%; box-sizing: border-box;
  background: var(--green); border: 2px solid var(--cream);
}
.manager-header__text { display: flex; flex-direction: column; min-width: 0; }
.manager-header__name {
  font-family: var(--font-body); font-weight: 500; font-size: 16px; line-height: 1.4; letter-spacing: -0.02em;
  color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
</style>
